import { useCallback, useEffect, useMemo, useState, memo } from "react";
import { applyChange } from '../../../utils/loadPositions';

const getNeighbours = (caixa, json, maxDist = 100, pestanya = null) => {
    const neighbours = [];
    const [x, y] = caixa.box.transform.slice(-2);

    Object.entries(json)
        .filter(([id, caixa]) => caixa.type === 'caixa')
        .filter(([id, caixa]) => !pestanya || (caixa.pestanya || 'pinya').toLowerCase() === pestanya)
        .forEach(([id, caixa]) => {
            const [x2, y2] = caixa.box.transform.slice(-2);
            const dist = Math.sqrt((x2 - x) ** 2 + (y2 - y) ** 2);

            if (dist < maxDist) {
                neighbours.push(id);
            }
        });

    return neighbours;
};

const getLinearRegression = (caixa1, caixa2, threshold = 15) => {
    const [x1, y1] = caixa1.box.transform.slice(-2);
    const [x2, y2] = caixa2.box.transform.slice(-2);

    if (Math.abs(x2 - x1) < 1) {
        return (x, y) => Math.abs(x - x1) < threshold;
    }

    const m = (y2 - y1) / (x2 - x1);
    const b = y1 - m * x1;

    return (x, y) => {
        const expectedY = m * x + b;
        return Math.abs(y - expectedY) < threshold;
    };
};

const checkAndAddToFila = (id, isInFila, json, fila, maxDist = 100, pestanya = null) => {
    const caixa = json[id];
    const [x, y] = caixa.box.transform.slice(-2);

    if (isInFila(x, y) && !fila.includes(id)) {
        fila.push(id);

        const neighbours = getNeighbours(caixa, json, maxDist, pestanya);
        neighbours.forEach(neighbourId => checkAndAddToFila(neighbourId, isInFila, json, fila, maxDist, pestanya));
    }
};

const getFila = (id, isInFila, json, pestanya = null) => {
    const fila = [];
    checkAndAddToFila(id, isInFila, json, fila, 100, pestanya);
    return fila;
};

const getBoxCenter = caixa => caixa.box.transform.slice(-2).map(v => parseInt(v));

const sortFila = (fila, json, originId, directionVector) => {
    const [dx, dy] = directionVector;
    const magnitude = dx ** 2 + dy ** 2 || 1;
    const origin = getBoxCenter(json[originId]);

    const project = (point) => ((point[0] - origin[0]) * dx + (point[1] - origin[1]) * dy) / magnitude;

    return [...fila]
        .sort((id1, id2) => project(getBoxCenter(json[id1])) - project(getBoxCenter(json[id2])));
};

const getFilaEntreCaixes = (prevId, currentId, json, pestanya = null) => {
    if (!(prevId in json) || !(currentId in json)) return null;

    const caixa = json[prevId];
    const neighbour = json[currentId];
    const isInFila = getLinearRegression(caixa, neighbour);

    const fila = getFila(prevId, isInFila, json, pestanya);
    if (!fila.includes(currentId)) return null;

    const [prevX, prevY] = getBoxCenter(caixa);
    const [currX, currY] = getBoxCenter(neighbour);
    const orderedFila = sortFila(fila, json, prevId, [currX - prevX, currY - prevY]);

    return orderedFila;
};

const SwapButton = memo((props) => {
    const { caixaSelected, setCaixaSelected, lastCaixes, setLastCaixes, posicions } = props;

    const swap = (props) => {
        const { socket, castell, posicions } = props;
        const castellerPrev = posicions.caixes[lastCaixes.prev];
        const castellerCurr = lastCaixes.current in posicions.caixes ? posicions.caixes[lastCaixes.current] : '_EMPTY_';

        // Set same action_id for both changes
        const room = `${props.selectedEvent}.${props.selectedBundle}.${props.selectedVersio}`;
        const action_id = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

        if (castellerPrev !== -1) {
            applyChange(props, `${lastCaixes.current},${castellerPrev}`);
            socket.emit('.save_change_to_room', room, `${lastCaixes.current},${castellerPrev}`, action_id);
        }
        if (castellerCurr !== -1) {
            applyChange(props, `${lastCaixes.prev},${castellerCurr}`);
            socket.emit('.save_change_to_room', room, `${lastCaixes.prev},${castellerCurr}`, action_id);
        }

        setCaixaSelected(-1);
    }

    const filaEntreCaixes = useMemo(
        () => getFilaEntreCaixes(lastCaixes.prev, lastCaixes.current, props.json || {}, props.pestanya?.toLowerCase() || null),
        [lastCaixes, props.json, props.pestanya]
    );

    const possibleAdvance = useMemo(() => {
        if (!filaEntreCaixes) return false;

        const prevIndex = filaEntreCaixes.indexOf(lastCaixes.prev);
        const currIndex = filaEntreCaixes.indexOf(lastCaixes.current);

        return prevIndex !== -1 && currIndex !== -1 && prevIndex < currIndex;
    }, [filaEntreCaixes, lastCaixes]);

    const advance = useCallback(() => {
        if (!possibleAdvance || !filaEntreCaixes) return;

        const { socket, posicions } = props;
        const prevIndex = filaEntreCaixes.indexOf(lastCaixes.prev);
        const currIndex = filaEntreCaixes.indexOf(lastCaixes.current);

        if (prevIndex === -1 || currIndex === -1 || prevIndex >= currIndex) return;

        const filaSegment = filaEntreCaixes.slice(prevIndex, currIndex + 1);
        const ocupants = filaSegment.map(id => id in posicions.caixes ? posicions.caixes[id] : '_EMPTY_');
        const moving = ocupants.shift();

        if (moving === '_EMPTY_') return;

        ocupants.splice(currIndex - prevIndex, 0, moving);

        const room = `${props.selectedEvent}.${props.selectedBundle}.${props.selectedVersio}`;
        const action_id = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

        filaSegment.forEach((caixaId, i) => {
            const newUser = ocupants[i];
            const currentUser = caixaId in posicions.caixes ? posicions.caixes[caixaId] : '_EMPTY_';

            if (newUser === undefined || newUser === currentUser) return;

            applyChange(props, `${caixaId},${newUser}`);
            socket.emit('.save_change_to_room', room, `${caixaId},${newUser}`, action_id);
        });

        setCaixaSelected(-1);
    }, [possibleAdvance, filaEntreCaixes, props, lastCaixes, setCaixaSelected]);

    const areSame = lastCaixes.prev === lastCaixes.current;
    const areBothCaixes = lastCaixes.prev !== -1 && lastCaixes.current !== -1;
    const prevHadAssigned = lastCaixes.prev in posicions.caixes;
    const possibleSwap = !areSame && areBothCaixes && prevHadAssigned;

    useEffect(() => {
        const swapWithEnter = ev => (ev.key === 'Enter' && possibleSwap) && swap(props)
        document.addEventListener('keyup', swapWithEnter);
        return () => document.removeEventListener('keyup', swapWithEnter);
    }, [possibleSwap]);

    useEffect(() => setLastCaixes(prevLastCaixes => {
        const emptyCaixa = caixaSelected !== -1 && !(lastCaixes.current in posicions.caixes);
        return { 'prev': emptyCaixa && prevLastCaixes.current !== -1 ? prevLastCaixes.prev : prevLastCaixes.current, 'current': caixaSelected };
    }), [caixaSelected]);

    useEffect(() => {
        const toSwapEl = document.querySelector('.toSwap');
        const caixaEl = document.getElementById(`caixa-${lastCaixes.prev}`);

        if (possibleSwap) {
            if (toSwapEl) toSwapEl.classList.remove('toSwap');
            if (caixaEl) caixaEl.classList.add('toSwap');
        } else {
            if (toSwapEl) toSwapEl.classList.remove('toSwap');
        }
    }, [possibleSwap, lastCaixes]);

    return (
        <>
            <div
                className={`floating-button swap ${!possibleAdvance || props.pestanya?.toLowerCase() !== 'pinya' ? 'hidden' : ''}`}
                onClick={advance}
            >
                ⏫
            </div>
            <div
                className={`floating-button swap ${!possibleSwap ? 'hidden' : ''}`}
                onClick={() => swap(props)}
            >
                { !(lastCaixes.current in posicions.caixes) ? <>&#10145;&#65039;</> : <>🔃</> }
            </div>
        </>
    );
}, (prevProps, nextProps) =>
    prevProps.caixaSelected === nextProps.caixaSelected &&
    prevProps.pestanya === nextProps.pestanya &&
    JSON.stringify(prevProps.lastCaixes) === JSON.stringify(nextProps.lastCaixes) &&
    JSON.stringify(prevProps.posicions) === JSON.stringify(nextProps.posicions)
);

export default SwapButton;